// The game's own map icons (SPEC §6.13): members of the client's UI texture atlas
// (UiTextureAtlasMember), cut out per build like the rest of the map art and served behind login
// at `/api/map/icons/:build/:name.png`. The server picks one for every pin; the page only draws it.

/** Player classes with an icon in the client (`classicon-<class>`). */
export const CLASS_ICONS = [
  'classicon-warrior',
  'classicon-paladin',
  'classicon-hunter',
  'classicon-rogue',
  'classicon-priest',
  'classicon-deathknight',
  'classicon-shaman',
  'classicon-mage',
  'classicon-warlock',
  'classicon-monk',
  'classicon-druid',
  'classicon-demonhunter',
  'classicon-evoker',
] as const;

export const MAP_ICONS = [
  // Flight masters as the game's flight map shows them: by faction once known, else undiscovered.
  'taxinode_horde',
  'taxinode_alliance',
  'taxinode_neutral',
  'taxinode_undiscovered',
  // Rares, elites and treasure, as the game's vignettes.
  'vignettekill',
  'vignettekillelite',
  'vignetteloot',
  // Dungeons: the entrance portal, bosses, ways between floors.
  'dungeon',
  'dungeonskull',
  'poi-door',
  'poi-door-arrow-up',
  'poi-door-arrow-down',
  // States drawn over another icon.
  'map-markeddefeated',
  'groupfinder-icon-greencheckmark',
  // The rest.
  'playerdeadblip',
  'playerpartyblip',
  'innkeeper',
  'loreobject-32x32',
  'fishing-hole',
  'crosshair_mine_32',
  'crosshair_gatherherbs_32',
  'crosshair_lootall_32',
  'crosshair_directions_32',
  'Waypoint-MapPin-Tracked',
  // Photos (M12): the client's screenshot icon, a framed picture.
  'WoWShare-ScreenshotIcon',
  'groupfinder-icon-class-hunter',
  ...CLASS_ICONS,
] as const;
export type MapIcon = (typeof MAP_ICONS)[number];

/** A state drawn small over a pin's icon: a boss or rare killed, a dungeon cleared, a book found. */
export type MapBadge = 'defeated' | 'done';
export const BADGE_ICONS: Record<MapBadge, MapIcon> = {
  defeated: 'map-markeddefeated',
  done: 'groupfinder-icon-greencheckmark',
};

/** A character's class icon: "Hunter" → classicon-hunter; the party dot without a class. */
export function classIcon(className: string | null | undefined): MapIcon {
  const icon = `classicon-${(className ?? '').toLowerCase().replace(/[^a-z]/g, '')}`;
  return (CLASS_ICONS as readonly string[]).includes(icon) ? (icon as MapIcon) : 'playerpartyblip';
}

/** An icon's file name: the atlas name in lower case. */
export const iconFile = (icon: string) => `${icon.toLowerCase()}.png`;
